import { paths } from '@/constants'
import { Durations } from '@/constants/durations'
import { Seo } from '@/layout/Seo'
import { DefaultLayout, NextPageWithLayout } from '@/layouts'
import { getCollection, GUILD_MEMBERSHIP_NUMBERS_ID } from '@/services/CmsService/Collection'
import { Collection } from '@/types/codegen-contentful'
import { getGenericStaticPaths } from '@/utils/GetGenericStaticPaths'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { GuildImpactView } from '@/views/GuildImpactView'

interface GuildImpactPageProps {
  locale: string
  preview: boolean
  guildMembershipNumbers: Collection
}

const GuildImpactPage: NextPageWithLayout<GuildImpactPageProps> = ({ locale, guildMembershipNumbers }) => {
  const { t } = useTranslate('guild')
  const basePath = `${getBasePath(locale)}`
  const url = `${basePath}${paths.guild.impact}`
  const title = t('angelStudiosGuildImpactTitle', 'Angel Guild Impact | Angel Studios')
  const description = t(
    'angelStudiosGuildImpactMetaDescriptionV2',
    'Thousands of Guild members voted and gave feedback to help make these films a reality. Discover how these light-amplifying stories are inspiring real people and making a difference in their lives.',
  )
  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.guild.impact}
        title={title}
      />
      <GuildImpactView guildMembershipNumbers={guildMembershipNumbers} />
    </>
  )
}

GuildImpactPage.getLayout = (page) => {
  return <DefaultLayout>{page}</DefaultLayout>
}

export const getStaticPaths = getGenericStaticPaths

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/guild/impact/index.tsx' },
  async ({ locale, preview }) => {
    const guildMembershipNumbers = await getCollection({ locale, preview }, GUILD_MEMBERSHIP_NUMBERS_ID)

    return {
      props: {
        locale,
        preview,
        guildMembershipNumbers,
        ...(await loadTranslations(locale, ['common', 'guild', 'watch'])),
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export default GuildImpactPage
